import React, { useCallback, useEffect, useRef, useState } from "react";
import "./theme.css";
import { posApi, posErr } from "./posApi";
import { enqueue, allQueued, dequeue, markAttempt } from "./db";
import PinLogin from "./PinLogin";
import PosMain from "./PosMain";
import KdsAlerts from "./KdsAlerts";
import { MapPin, User, Clock, LogOut, ChevronDown, RefreshCw, Printer, Banknote, ArrowRightLeft } from "lucide-react";

const C = { bg: "#FAF7F0", surf: "#FFFFFF", surf2: "#FFFCF7", crimson: "#8B0000", crimsonD: "#6B0000", gold: "#B8860B", gold2: "#C9A84C", text: "#3B2722", muted: "#765A4A", border: "#E9DED0", success: "#4F7A52", error: "#A52222" };
const N = (n) => "₦" + Number(n || 0).toLocaleString("en-NG", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function PosApp() {
  const [user, setUser] = useState(() => { try { return JSON.parse(localStorage.getItem("pos_user") || "null"); } catch { return null; } });
  const [shift, setShift] = useState(() => { try { return JSON.parse(localStorage.getItem("pos_shift") || "null"); } catch { return null; } });
  const [tab, setTab] = useState("pos");
  const [online, setOnline] = useState(navigator.onLine);
  const [queueCount, setQueueCount] = useState(0);
  const [lastSync, setLastSync] = useState(null);
  const [clock, setClock] = useState(new Date());
  const [catalog, setCatalog] = useState({ products: [], categories: [], customers: [], modifierGroups: [], settings: {} });
  const [orders, setOrders] = useState([]);
  const [orderNo, setOrderNo] = useState("SM-000000");
  const syncing = useRef(false);

  const authed = !!(user && shift && localStorage.getItem("pos_token"));
  const canDiscount = !!(user && (user.is_admin || (user.role?.tier ?? 5) <= 4));
  const [cashPos, setCashPos] = useState(null);
  const [showMovement, setShowMovement] = useState(false);
  const loadCashPos = useCallback(() => {
    if (!shift) return;
    posApi.get("/tills/cash-position", { params: { till: shift.till, location_id: shift.location_id } })
      .then(({ data }) => setCashPos(data)).catch(() => {});
  }, [shift]);

  const refreshQueue = useCallback(async () => { setQueueCount((await allQueued()).length); }, []);
  const loadOrders = useCallback(() => { posApi.get("/orders").then(({ data }) => { const mine = shift ? data.filter((o) => o.shift_id === shift.id) : []; setOrders(mine); setOrderNo(`SM-${String((data[0]?.number || "").replace(/\D/g, "") ? Number(data[0].number.replace(/\D/g, "")) + 1 : 1).padStart(6, "0")}`); }).catch(() => {}); }, [shift]);

  const sync = useCallback(async () => {
    if (syncing.current || !navigator.onLine) return;
    const q = await allQueued(); if (!q.length) return;
    syncing.current = true;
    try {
      const { data } = await posApi.post("/pos/sync", { transactions: q.map((t) => t.payload) });
      for (const r of data.results) { if (r.status === "ok") await dequeue(r.client_txn_id); else await markAttempt(r.client_txn_id, r.error); }
      setLastSync(new Date());
    } catch (e) { /* still offline — retry later */ }
    finally { syncing.current = false; await refreshQueue(); loadOrders(); }
  }, [refreshQueue, loadOrders]);

  useEffect(() => { if (user) localStorage.setItem("pos_user", JSON.stringify(user)); }, [user]);
  useEffect(() => { if (shift) localStorage.setItem("pos_shift", JSON.stringify(shift)); }, [shift]);
  useEffect(() => {
    const t = setInterval(() => setClock(new Date()), 1000);
    const up = () => { setOnline(true); sync(); }; const down = () => setOnline(false);
    window.addEventListener("online", up); window.addEventListener("offline", down);
    const iv = setInterval(sync, 12000); refreshQueue();
    return () => { clearInterval(t); clearInterval(iv); window.removeEventListener("online", up); window.removeEventListener("offline", down); };
  }, [sync, refreshQueue]);
  useEffect(() => {
    if (!authed) return;
    Promise.all([
      posApi.get("/products?product_type=finished"), posApi.get("/products?product_type=stock"), posApi.get("/products?product_type=combo"),
      posApi.get("/categories"), posApi.get("/customers"), posApi.get("/modifier-groups").catch(() => ({ data: [] })), posApi.get("/settings/business").catch(() => ({ data: {} })),
    ]).then(([f, s, c, cat, cu, mg, biz]) => setCatalog({ products: [...f.data, ...s.data, ...c.data], categories: cat.data, customers: cu.data, modifierGroups: mg.data, settings: biz.data || {} })).catch(() => {});
    loadOrders(); sync(); loadCashPos();
    const pi = setInterval(loadCashPos, 15000);
    return () => clearInterval(pi);
  }, [authed, loadOrders, sync, loadCashPos]);

  const submitSale = async (txn) => {
    if (navigator.onLine) {
      try { const { data } = await posApi.post("/pos/checkout", txn); await refreshQueue(); loadOrders(); return { ok: true, order: data, offline: false }; }
      catch (e) { if (e.response) return { ok: false, error: posErr(e) }; }
    }
    await enqueue({ client_txn_id: txn.client_txn_id, payload: txn, at: Date.now(), attempts: 0 });
    await refreshQueue();
    return { ok: true, order: null, offline: true };
  };
  const logout = () => { ["pos_token", "pos_user", "pos_shift"].forEach((k) => localStorage.removeItem(k)); setUser(null); setShift(null); setTab("pos"); };

  if (!authed) return <PinLogin onDone={(u, s) => { setUser(u); setShift(s); setTab("pos"); localStorage.setItem("pos_user", JSON.stringify(u)); localStorage.setItem("pos_shift", JSON.stringify(s)); }} />;

  return (
    <div className="pos-root">
      <div style={{ height: 78, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 22px", background: C.surf, borderBottom: `1px solid ${C.border}`, flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 22 }}>
          <img src="/logo.jpg" alt="Sir Monty" style={{ height: 44, width: "auto", objectFit: "contain" }} />
          <div style={{ display: "flex", gap: 20 }}>
            <Meta icon={<MapPin size={16} color={C.gold} />} top={shift.location_name || "Location"} bottom={shift.till} />
            <Meta icon={<User size={16} color={C.gold} />} top={user.role?.name || "Cashier"} bottom={user.name} />
          </div>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          {[["pos", "POS"], ["activity", "Activity"], ["reports", "Reports"]].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`postab-${k}`} className="label"
              style={{ padding: "10px 22px", borderRadius: 10, cursor: "pointer", fontSize: 12, fontWeight: 700, background: tab === k ? C.crimson : "transparent", color: tab === k ? "#fff" : C.muted, border: "none" }}>{l}</button>
          ))}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, color: C.text }}>
            <Clock size={16} color={C.muted} />
            <div><div className="display" style={{ fontSize: 17, fontWeight: 700, lineHeight: 1 }}>{clock.toLocaleTimeString("en-NG", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}</div>
              <div style={{ fontSize: 10.5, color: C.muted }}>{clock.toLocaleDateString("en-NG", { weekday: "short", day: "2-digit", month: "short", year: "numeric" })}</div></div>
          </div>
          <span data-testid="pos-connection" className="label" style={{ fontSize: 10, padding: "6px 12px", borderRadius: 20, background: online ? "rgba(79,122,82,.12)" : "rgba(165,34,34,.1)", color: online ? C.success : C.error, border: `1px solid ${online ? "#bcd6bd" : "#e6bcbc"}`, fontWeight: 700 }}>
            ● {online ? "Online" : "Offline"}{queueCount ? ` · ${queueCount}` : ""}</span>
          <KdsAlerts C={C} />
          {cashPos && (
            <button onClick={() => setShowMovement(true)} data-testid="pos-cash-pill" title="TILL cash position — tap to request cash movement"
              style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 12px", borderRadius: 20,
                background: cashPos.over_limit ? "rgba(165,34,34,.1)" : "rgba(184,134,11,.1)",
                border: `1px solid ${cashPos.over_limit ? "#e6bcbc" : "#e9d9b0"}`,
                color: cashPos.over_limit ? C.error : C.gold, cursor: "pointer", fontWeight: 700, fontSize: 11 }}>
              <Banknote size={13} />
              <span>₦{Number(cashPos.cash_position).toLocaleString("en-NG")}</span>
              {cashPos.max_cash_limit > 0 && <span style={{ opacity: .7 }}>/ ₦{Number(cashPos.max_cash_limit).toLocaleString("en-NG")}</span>}
              {cashPos.over_limit && <span style={{ background: C.error, color: "#fff", padding: "1px 6px", borderRadius: 6, fontSize: 9 }}>OVER</span>}
            </button>
          )}
          <button onClick={() => window.open("/pos/customer-display", "sirmonty_cd", "width=1280,height=800")}
            data-testid="open-customer-display"
            title="Open customer-facing display in a new window"
            style={{ background: "transparent", border: `1px solid ${C.border}`, color: C.muted, borderRadius: 9, padding: 9, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
            <Printer size={14} style={{ display: "none" }} />
            <span className="label" style={{ fontSize: 10, fontWeight: 700 }}>CUSTOMER VIEW</span>
          </button>
          <span className="label" style={{ fontSize: 11, padding: "8px 12px", borderRadius: 9, border: `1px solid ${C.border}`, color: C.muted, display: "flex", alignItems: "center", gap: 6 }}>Shift <ChevronDown size={13} /></span>
          <button onClick={logout} data-testid="pos-exit" style={{ background: "transparent", border: `1px solid ${C.border}`, color: C.muted, borderRadius: 9, padding: 9, cursor: "pointer", display: "flex" }}><LogOut size={16} /></button>
        </div>
      </div>

      {tab === "pos" && <PosMain user={user} shift={shift} {...catalog} submitSale={submitSale} onSold={() => { refreshQueue(); loadOrders(); loadCashPos(); }} canDiscount={canDiscount} orderNo={orderNo} />}
      {tab === "activity" && <Activity orders={orders} queueCount={queueCount} onRefresh={() => { loadOrders(); refreshQueue(); }} />}
      {tab === "reports" && <Reports shift={shift} orders={orders} onClosed={logout} />}
      {showMovement && <CashMovementModal shift={shift} user={user} cashPos={cashPos} onClose={() => { setShowMovement(false); loadCashPos(); }} />}

      <div style={{ height: 40, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 22px", background: C.surf2, borderTop: `1px solid ${C.border}`, fontSize: 11, color: C.muted }}>
        <span className="display" style={{ color: C.crimson, fontWeight: 700 }}>Sir Monty POS <span style={{ color: C.muted, fontWeight: 400 }}>v1.0.0</span></span>
        <span>All prices in NGN · VAT {catalog.settings?.default_tax_rate ?? 7.5}% · Service {catalog.settings?.service_charge_rate ?? 0}%</span>
        <span style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <span><RefreshCw size={11} style={{ verticalAlign: "-1px" }} /> Last sync: {lastSync ? lastSync.toLocaleTimeString("en-NG", { hour: "2-digit", minute: "2-digit" }) : "—"}</span>
          <button style={{ display: "flex", alignItems: "center", gap: 6, background: C.surf, border: `1px solid ${C.border}`, borderRadius: 8, padding: "6px 12px", cursor: "pointer", color: C.text }} className="label"><Printer size={12} /> Open Cash Drawer</button>
        </span>
      </div>
    </div>
  );
}

function Meta({ icon, top, bottom }) {
  return <div style={{ display: "flex", alignItems: "center", gap: 8 }}>{icon}<div><div style={{ fontSize: 13, fontWeight: 600, color: "#3B2722", lineHeight: 1.1 }}>{top}</div><div style={{ fontSize: 11, color: "#765A4A" }}>{bottom}</div></div></div>;
}

function Activity({ orders, queueCount, onRefresh }) {
  const [queued, setQueued] = useState([]);
  const [detail, setDetail] = useState(null);
  const [loadingId, setLoadingId] = useState(null);
  useEffect(() => { allQueued().then(setQueued); }, [queueCount]);
  const openDetail = async (o) => {
    setLoadingId(o.id);
    try { const { data } = await posApi.get(`/orders/${o.id}`); setDetail(data); }
    catch { setDetail(o); }
    finally { setLoadingId(null); }
  };
  return (
    <div className="pos-scroll" style={{ flex: 1, padding: 28 }} data-testid="pos-activity">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
        <div className="display" style={{ fontSize: 27, fontWeight: 700, color: C.text }}>Shift Activity</div>
        <button onClick={onRefresh} style={btn} data-testid="activity-refresh">Refresh</button>
      </div>
      {queued.length > 0 && (<div style={{ marginBottom: 22 }}>
        <div className="label" style={{ fontSize: 11, color: C.gold, marginBottom: 8 }}>Pending sync ({queued.length})</div>
        {queued.map((q) => <div key={q.client_txn_id} data-testid="queued-txn" style={{ ...card, borderColor: C.gold }}>
          <span>{q.payload.customer_name} · {q.payload.items.length} items</span><span style={{ color: C.gold }}>⏳ queued{q.attempts ? ` · ${q.attempts} tries` : ""}</span></div>)}
      </div>)}
      <div className="label" style={{ fontSize: 11, color: C.muted, marginBottom: 8 }}>Completed ({orders.length})</div>
      {orders.length === 0 ? <div style={{ color: C.muted }}>No sales yet this shift.</div> : orders.map((o) => (
        <div key={o.id} data-testid="activity-order" onClick={() => openDetail(o)}
          style={{ ...card, cursor: "pointer" }} title="View full order details">
          <span style={{ color: C.crimson, fontWeight: 700 }}>{o.number}</span><span>{o.customer_name}</span>
          <span style={{ textTransform: "capitalize" }}>{(o.order_type || "").replace("_", " ")}</span>
          <span style={{ fontWeight: 700 }}>{N(o.total)}{loadingId === o.id ? " …" : ""}</span></div>))}
      {detail && <OrderDetailModal order={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}

export function OrderDetailModal({ order, onClose }) {
  const L = (order.items || order.lines || []);
  return (
    <div onClick={onClose} data-testid="order-detail-modal"
      style={{ position: "fixed", inset: 0, background: "rgba(30,18,14,.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 90, padding: 20 }}>
      <div onClick={(e) => e.stopPropagation()} className="pos-scroll"
        style={{ background: "#FFFCF7", borderRadius: 18, maxWidth: 560, width: "100%", maxHeight: "86vh", overflowY: "auto", padding: 26, border: `1px solid ${C.border}` }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
          <div>
            <div className="display" style={{ fontSize: 22, fontWeight: 700, color: C.crimson }}>{order.number}</div>
            <div style={{ fontSize: 12, color: C.muted }}>
              {(order.order_type || "").replace("_", " ")} · {order.status || "completed"} · {new Date(order.created_at || Date.now()).toLocaleString()}
            </div>
          </div>
          <button onClick={onClose} style={btn} data-testid="order-detail-close">Close</button>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, fontSize: 12, color: C.text, marginBottom: 16 }}>
          <div><b>Customer</b><div>{order.customer_name || "Walk-in"}</div></div>
          <div><b>Served by</b><div data-testid="order-detail-agent">{order.served_by_name || order.cashier_name || "—"}</div></div>
          <div><b>Till</b><div>{order.till || "—"}</div></div>
          <div><b>Table</b><div>{order.table || "—"}</div></div>
        </div>
        <div className="label" style={{ fontSize: 11, color: C.muted, marginBottom: 6 }}>Items ({L.length})</div>
        <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }} data-testid="order-detail-items">
          <tbody>
            {L.map((it, i) => (
              <tr key={i} style={{ borderBottom: `1px solid ${C.border}` }}>
                <td style={{ padding: "8px 4px" }}>
                  {it.name}{it.size ? ` · ${it.size}` : ""}
                  {(it.modifiers || []).length > 0 && <div style={{ fontSize: 11, color: C.muted }}>+ {(it.modifiers || []).map((m) => m.name || m).join(", ")}</div>}
                  {it.note && <div style={{ fontSize: 11, color: C.gold }}>Note: {it.note}</div>}
                </td>
                <td style={{ padding: "8px 4px", textAlign: "center", width: 50 }}>×{it.quantity}</td>
                <td style={{ padding: "8px 4px", textAlign: "right", width: 90 }}>{N((it.unit_price || it.price || 0) * (it.quantity || 1))}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ marginTop: 14, fontSize: 13 }}>
          {[["Subtotal", order.subtotal], ["Discount", -(order.discount || 0)], ["Service charge", order.service_charge], ["VAT", order.tax]]
            .filter(([, v]) => Number(v || 0) !== 0)
            .map(([k, v]) => <Row key={k} k={k} v={N(v)} />)}
          <Row k="Total" v={N(order.total)} strong />
          {(order.payments || []).map((p, i) => <Row key={i} k={`Paid · ${p.method}`} v={N(p.amount)} />)}
          {order.notes && <div style={{ marginTop: 10, fontSize: 12, color: C.muted }}>Order note: {order.notes}</div>}
        </div>
      </div>
    </div>
  );
}

function Row({ k, v, strong }) {
  return <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", fontWeight: strong ? 700 : 400, color: strong ? C.crimson : C.text }}><span>{k}</span><span>{v}</span></div>;
}

function Reports({ shift, orders, onClosed }) {
  const [cash, setCash] = useState("");
  const [card, setCard] = useState("");
  const [transfer, setTransfer] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [supervisor, setSupervisor] = useState(null);
  const [showAuth, setShowAuth] = useState(false);
  const [authPin, setAuthPin] = useState("");
  const [authErr, setAuthErr] = useState("");

  const tot = { cash: 0, card: 0, transfer: 0, loyalty: 0 };
  orders.forEach((o) => (o.payments || []).forEach((p) => { tot[p.method] = (tot[p.method] || 0) + Number(p.amount || 0); }));
  const gross = orders.reduce((s, o) => s + o.total, 0);
  const expectedCash = tot.cash + (shift.opening_float || 0);
  const cashVar = Number(cash || 0) - expectedCash;
  const cardVar = Number(card || 0) - tot.card;
  const transferVar = Number(transfer || 0) - tot.transfer;
  const authorise = async () => {
    setAuthErr("");
    try {
      const { data } = await posApi.post("/pos/authorize", { pin: authPin, action: "shift_close" });
      setSupervisor(data.authorized_by);
      setShowAuth(false); setAuthPin("");
    } catch (e) { setAuthErr(posErr(e)); setAuthPin(""); }
  };
  const close = async () => {
    if (!supervisor) { setShowAuth(true); return; }
    setBusy(true);
    try {
      const { data } = await posApi.post(`/pos/shift/${shift.id}/close`, {
        counted_cash: Number(cash || 0),
        counted_card: Number(card || 0),
        counted_transfer: Number(transfer || 0),
      });
      setResult(data);
    } catch (e) { window.alert(posErr(e)); }
    finally { setBusy(false); }
  };
  const varColor = (v) => Math.abs(v) < 0.01 ? C.success : C.error;
  const varLabel = (v) => Math.abs(v) < 0.01 ? "MATCHED" : v > 0 ? `OVER ${N(v)}` : `SHORT ${N(-v)}`;
  return (
    <div className="pos-scroll" style={{ flex: 1, padding: 28, maxWidth: 720 }} data-testid="pos-reports">
      <div className="display" style={{ fontSize: 27, fontWeight: 700, marginBottom: 4, color: C.text }}>Shift Close & Reconciliation</div>
      <div style={{ color: C.muted, fontSize: 13, marginBottom: 20 }}>{shift.till} · opened {new Date(shift.opened_at).toLocaleString("en-NG")} · float {N(shift.opening_float)}</div>
      {supervisor && <div className="label" data-testid="supervisor-badge" style={{ display: "inline-block", padding: "5px 12px", borderRadius: 8, background: "rgba(79,122,82,.12)", color: C.success, marginBottom: 14, fontSize: 11, fontWeight: 700 }}>✓ SUPERVISOR AUTHORISED · {supervisor}</div>}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 12, marginBottom: 20 }}>
        {[["Cash Sales", tot.cash], ["Card Sales", tot.card], ["Transfer Sales", tot.transfer], ["Loyalty", tot.loyalty]].map(([l, v]) => (
          <div key={l} style={{ background: C.surf, border: `1px solid ${C.border}`, borderRadius: 12, padding: 16 }}>
            <div className="label" style={{ fontSize: 10, color: C.muted }}>{l}</div>
            <div className="display" style={{ fontSize: 24, fontWeight: 700, color: C.crimson }} data-testid={`tally-${l.toLowerCase().replace(/ /g,"-")}`}>{N(v)}</div></div>))}
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", padding: 14, background: C.surf, borderRadius: 12, border: `1px solid ${C.border}`, marginBottom: 16 }}>
        <span className="label" style={{ color: C.muted, fontSize: 12 }}>Gross Sales · {orders.length} orders</span><span className="display" style={{ fontSize: 22, fontWeight: 700 }}>{N(gross)}</span></div>
      {!result ? (<>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10, marginBottom: 12 }}>
          {[["Cash", cash, setCash, expectedCash, cashVar], ["Card", card, setCard, tot.card, cardVar], ["Transfer", transfer, setTransfer, tot.transfer, transferVar]].map(([l, v, sf, exp, vr]) => (
            <div key={l} style={{ background: C.surf, borderRadius: 12, border: `1px solid ${C.border}`, padding: 12 }}>
              <div className="label" style={{ fontSize: 10, color: C.muted }}>Counted {l}</div>
              <input type="number" value={v} onChange={(e) => sf(e.target.value)} disabled={!supervisor} data-testid={`counted-${l.toLowerCase()}`}
                style={{ width: "100%", background: C.surf2, border: `1px solid ${C.border}`, borderRadius: 8, padding: 10, color: C.text, fontSize: 17, marginTop: 6, opacity: supervisor ? 1 : .6 }} />
              <div style={{ fontSize: 11, color: C.muted, marginTop: 6 }}>Expected {N(exp)}</div>
              {v !== "" && <div data-testid={`variance-${l.toLowerCase()}`} style={{ marginTop: 4, fontSize: 12, fontWeight: 700, color: varColor(vr) }}>{varLabel(vr)}</div>}
            </div>
          ))}
        </div>
        {!supervisor ? (
          <button onClick={() => setShowAuth(true)} data-testid="unlock-supervisor-btn" className="label"
            style={{ width: "100%", padding: 16, borderRadius: 12, border: "none", cursor: "pointer", background: C.gold, color: "#fff", fontWeight: 800, letterSpacing: ".05em", marginTop: 8 }}>UNLOCK WITH SUPERVISOR PIN</button>
        ) : (
          <button onClick={close} disabled={busy || cash === ""} data-testid="close-shift-btn" className="label"
            style={{ width: "100%", padding: 16, borderRadius: 12, border: "none", cursor: "pointer", background: C.crimson, color: "#fff", fontWeight: 800, letterSpacing: ".05em", marginTop: 8, opacity: cash === "" ? 0.5 : 1 }}>{busy ? "CLOSING…" : "CLOSE SHIFT & RECONCILE"}</button>
        )}
      </>) : (
        <div style={{ padding: 20 }} data-testid="shift-closed">
          <div className="display" style={{ fontSize: 26, fontWeight: 700, color: C.crimson, textAlign: "center" }}>Shift Closed</div>
          <div style={{ marginTop: 16, background: C.surf, border: `1px solid ${C.border}`, borderRadius: 12, padding: 18 }}>
            <div style={{ fontSize: 12, color: C.muted, marginBottom: 10 }}>Reconciliation summary</div>
            {["cash", "card", "transfer"].map((k) => {
              const v = result?.shift_report?.reconciliation?.[k];
              if (!v) return null;
              return <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: `1px dashed ${C.border}` }}>
                <span style={{ textTransform: "capitalize" }}>{k}</span>
                <span>Expected {N(v.expected)} · Actual {N(v.actual)} · <b style={{ color: varColor(v.variance) }}>{v.label.toUpperCase()}{Math.abs(v.variance) > 0 ? ` ${N(Math.abs(v.variance))}` : ""}</b></span>
              </div>;
            })}
          </div>
          <button onClick={onClosed} style={{ marginTop: 20, width: "100%", padding: 14, borderRadius: 12, background: C.crimson, color: "#fff", border: "none", cursor: "pointer", fontWeight: 800 }}>Sign Out</button>
        </div>)}
      {showAuth && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(59,39,34,.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 90 }} data-testid="supervisor-auth-modal">
          <div style={{ width: 340, background: "#fff", borderRadius: 16, padding: 24 }}>
            <div className="display" style={{ fontSize: 22, fontWeight: 700, color: C.crimson }}>Supervisor Authorisation</div>
            <div style={{ color: C.muted, fontSize: 13, marginTop: 4, marginBottom: 14 }}>Only a Supervisor (tier 4 or above) can close a shift.</div>
            <input type="password" inputMode="numeric" value={authPin} onChange={(e) => setAuthPin(e.target.value.replace(/\D/g, ""))} autoFocus data-testid="supervisor-pin"
              placeholder="••••" style={{ width: "100%", background: C.surf2, border: `1px solid ${C.border}`, borderRadius: 10, padding: 12, color: C.text, fontSize: 22, letterSpacing: 6, textAlign: "center" }} />
            {authErr && <div style={{ color: C.error, fontSize: 12, marginTop: 8 }} data-testid="supervisor-err">{authErr}</div>}
            <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
              <button onClick={() => setShowAuth(false)} style={{ flex: 1, padding: 12, borderRadius: 10, background: "#fff", border: `1px solid ${C.border}`, cursor: "pointer", color: C.muted }}>Cancel</button>
              <button onClick={authorise} disabled={authPin.length < 3} data-testid="supervisor-authorise" className="label"
                style={{ flex: 2, padding: 12, borderRadius: 10, background: C.crimson, color: "#fff", border: "none", cursor: "pointer", fontWeight: 700, opacity: authPin.length < 3 ? .5 : 1 }}>AUTHORISE</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const btn = { padding: "9px 16px", borderRadius: 9, background: "#FFF", border: `1px solid ${C.border}`, color: C.text, cursor: "pointer", fontSize: 13 };
const card = { display: "flex", justifyContent: "space-between", gap: 12, padding: "13px 15px", background: "#FFF", border: `1px solid ${C.border}`, borderRadius: 10, marginBottom: 8, fontSize: 13 };

function CashMovementModal({ shift, user, cashPos, onClose }) {
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [direction, setDirection] = useState("out");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null); // pending / approved / rejected
  const [reqId, setReqId] = useState(null);
  const submit = async () => {
    if (!amount || !reason) return;
    setBusy(true);
    try {
      const { data } = await posApi.post("/tills/cash-movement-request", {
        till: shift.till, location_id: shift.location_id,
        direction, amount: Number(amount), reason,
      });
      setReqId(data.id); setStatus("pending");
    } catch (e) { window.alert(posErr(e)); }
    finally { setBusy(false); }
  };
  useEffect(() => {
    if (!reqId) return;
    const iv = setInterval(async () => {
      try {
        const { data } = await posApi.get(`/approvals/requests/${reqId}`);
        if (data.status !== "PENDING") { setStatus(data.status.toLowerCase()); clearInterval(iv); }
      } catch { /* ignore */ }
    }, 4000);
    return () => clearInterval(iv);
  }, [reqId]);
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(59,39,34,.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 90 }} data-testid="cash-movement-modal">
      <div style={{ width: 420, background: "#fff", borderRadius: 16, padding: 24, color: C.text }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <ArrowRightLeft size={20} color={C.crimson} />
          <div className="display" style={{ fontSize: 22, fontWeight: 700, color: C.crimson }}>Cash Movement Request</div>
        </div>
        <div style={{ color: C.muted, fontSize: 12, marginTop: 4 }}>Routed via SUPERVISOR → LOCATION MANAGER → REGIONAL MANAGER approval chain.</div>
        <div style={{ marginTop: 14, padding: 12, background: C.surf2, borderRadius: 10, border: `1px solid ${C.border}` }}>
          <div style={{ fontSize: 11, color: C.muted }}>{shift.till} · {shift.location_name}</div>
          {cashPos && <div style={{ marginTop: 4, fontSize: 13 }}>
            Position <b>{N(cashPos.cash_position)}</b> · Limit {N(cashPos.max_cash_limit)}{cashPos.over_limit && <span style={{ color: C.error, fontWeight: 700 }}> · OVER by {N(cashPos.over_by)}</span>}
          </div>}
        </div>
        {!reqId ? (<>
          <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
            {[["out", "REMOVE (safe drop)"], ["in", "ADD (float boost)"]].map(([k, l]) => (
              <button key={k} onClick={() => setDirection(k)} data-testid={`cm-dir-${k}`}
                style={{ flex: 1, padding: 10, borderRadius: 10, cursor: "pointer", background: direction === k ? C.crimson : C.surf2, color: direction === k ? "#fff" : C.text, border: `1px solid ${direction === k ? C.crimson : C.border}`, fontWeight: 700, fontSize: 11 }}>{l}</button>
            ))}
          </div>
          <div className="label" style={{ fontSize: 11, color: C.muted, marginTop: 12 }}>Amount (₦)</div>
          <input type="number" min="0" step="1000" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="cm-amount"
            style={{ width: "100%", background: C.surf2, border: `1px solid ${C.border}`, borderRadius: 10, padding: 12, color: C.text, fontSize: 18, marginTop: 6 }} />
          <div className="label" style={{ fontSize: 11, color: C.muted, marginTop: 12 }}>Reason</div>
          <textarea value={reason} onChange={(e) => setReason(e.target.value)} data-testid="cm-reason" rows={3}
            placeholder="e.g. Above cash limit, sweep to safe"
            style={{ width: "100%", background: C.surf2, border: `1px solid ${C.border}`, borderRadius: 10, padding: 10, color: C.text, fontSize: 13, marginTop: 6, resize: "vertical" }} />
          <div style={{ display: "flex", gap: 10, marginTop: 18 }}>
            <button onClick={onClose} style={{ flex: 1, padding: 12, borderRadius: 10, background: "#fff", border: `1px solid ${C.border}`, cursor: "pointer", color: C.muted }}>Cancel</button>
            <button onClick={submit} disabled={busy || !amount || !reason} data-testid="cm-submit"
              style={{ flex: 2, padding: 12, borderRadius: 10, background: C.crimson, color: "#fff", border: "none", cursor: "pointer", fontWeight: 700, opacity: (!amount || !reason) ? .5 : 1 }}>{busy ? "SUBMITTING…" : "SUBMIT REQUEST"}</button>
          </div>
        </>) : (
          <div style={{ padding: "20px 0", textAlign: "center" }} data-testid={`cm-status-${status}`}>
            {status === "pending" && <>
              <div style={{ fontSize: 15, fontWeight: 600, color: C.gold }}>Waiting for approval chain…</div>
              <div style={{ fontSize: 12, color: C.muted, marginTop: 6 }}>SUPERVISOR → LOCATION MANAGER → REGIONAL MANAGER</div>
            </>}
            {status === "approved" && <div style={{ fontSize: 16, fontWeight: 700, color: C.success }}>✓ APPROVED — cash moved</div>}
            {status === "rejected" && <div style={{ fontSize: 16, fontWeight: 700, color: C.error }}>Rejected</div>}
            <button onClick={onClose} style={{ marginTop: 14, padding: "10px 22px", borderRadius: 10, background: C.crimson, color: "#fff", border: "none", cursor: "pointer" }}>Close</button>
          </div>
        )}
      </div>
    </div>
  );
}
